import React from 'react';

import Box from '@mui/material/Box';

import { CheckboxList } from 'client/app/apps/disco-prototype/components/PlateMapCanvas/coloring/CheckboxMixer/CheckboxList';
import { extractWeights } from 'client/app/apps/disco-prototype/components/PlateMapCanvas/coloring/extractWeights';
import { mixLinearRGB } from 'client/app/apps/disco-prototype/components/PlateMapCanvas/coloring/gammaCorrectedRgbMixer';
import { Table } from 'common/types/disco/table';

type Well = {
  id: string;
  color: string;
};

type Props = {
  colors: string[];
  weightNames: string[];
  labels?: string[];
  location: string;
  data?: Table;
  render: (wells: Well[]) => React.ReactNode;
};

const CheckboxMixer = ({
  colors,
  weightNames,
  labels,
  location,
  data,
  render,
}: Props) => {
  const [selected, setSelected] = React.useState<string[]>(
    weightNames.map((_, i) => `${i}`),
  );
  const weightedRows = extractWeights(data, location, weightNames);
  const maxWeights = weightedRows.reduce<number[]>((acc, { weights }) => {
    if (acc.length === 0) return weights.slice();
    return weights.map((v, i) => Math.max(v, acc[i]));
  }, []);

  const wells =
    selected.length === 0
      ? weightedRows.map(row => {
          return {
            id: row.id,
            color: '#ffffff',
          };
        })
      : selected.length === 1
      ? weightedRows.map(row => {
          const idx = Number(selected[0]);
          const w = row.weights[idx] / maxWeights[idx];
          return {
            id: row.id,
            color: mixLinearRGB([colors[idx], '#ffffff'], [w, 1 - w]),
          };
        })
      : weightedRows.map(row => {
          const baseHexes = selected.map(idx => colors[Number(idx)]);
          const weights = selected.map(idx => row.weights[Number(idx)]);
          return {
            id: row.id,
            color: mixLinearRGB(baseHexes, weights),
          };
        });

  const options = weightNames.map((wn, index) => ({
    id: `${index}`,
    label: labels ? labels[index] : wn,
    color: colors[index],
  }));

  return (
    <Box display="flex" flex={1} flexDirection="row" gap={4}>
      <Box display="flex" flex={1}>
        {render(wells)}
      </Box>
      {colors.length > 1 && (
        <Box display="flex">
          <CheckboxList
            title="Subcomponents"
            options={options}
            value={selected}
            onChange={setSelected}
          />
        </Box>
      )}
    </Box>
  );
};

export default CheckboxMixer;
